import { MessageDialog, ObjectHelper, StringHelper } from "@bryntum/schedulerpro";
import { STORE_KEYS as StoreKeys } from "../constants/StoreKeys.jsx";

export const getCssVariableValue = (variable, fromEl = document.body) => {
  if (variable) {
    return getComputedStyle(fromEl).getPropertyValue(variable);
  }
}
export const canLoadPage = (event) => {
  if (Boolean(canLoadPage.project?.changes) || window.swivuInProgressNetworkRequests > 0) {
    event.preventDefault();
    // For Chrome
    event.returnValue = '';
    // For Firefox
    return "";
  }
}

// Note: this function only separates out inline style by semicolon and doesn't separate key and value
export const getInlineStyleInObj = (styles = "") => {
  return styles ? [...styles.match(/([\w-]+)\s*:\s*([^;]+)\s*;?/g)]: [];
}

const isBrowserExtensionError = (source) => {
  if (!source) return false;

  const extensionProtocols = [
    "chrome-extension://", // Chrome & Edge extensions
    "moz-extension://",    // Firefox extensions
    "safari-extension://", // Safari extensions
    "ms-browser-extension://" // Edge legacy extensions
  ];

  return extensionProtocols.some((protocol) => source.startsWith(protocol));
}


export const unHandleErrorListener = (project) => (event, ) => {

  const reasonMessage = event.reason?.message;

  // whenever there's a crash happen internal to bryntum then isAutoSyncSuspended (private property) flag gets true
  // isAutoSyncSuspended seems to be redundant in bryntum 6.0.2 but isChangeTrackingSuspended seems to be useful
  if (project?.isAutoSyncSuspended || project?.isChangeTrackingSuspended) {
    if (isBrowserExtensionError(event.reason?.stack || event.stack)) {
      console.error("The project auto-sync has already stopped, but the error is originating from the browser extension: ", reasonMessage);
    } else {
      MessageDialog.alert({
        title: "Unhandled Exception",
        message: `${reasonMessage}.<br />The application cannot recover from this error on its own, and any further changes will be lost. Please reload the page.`,
        okButton: "Reload",
      }).then(() => {
        window.location.reload();
      });
    }
  }
}


/**
 * Collects and stores the latest 5 transactions of received socket data for Sentry logging.
 *
 * This function:
 * - Parses the incoming socket data safely.
 * - Extracts relevant fields while removing unnecessary store data.
 * - Keeps only the latest 5 transactions in a global queue (`window.swivuSocketTransactionQueue`).
 *
 * @param {Object} socketData - The incoming socket data containing transaction details.
 */
export const collectTransactionsForSentryLog = (socketData) => {
  // Destructure relevant fields while extracting the rest into `stores`
  const {
    type,
    message,
    success,
    revision,
    board_session_id,
    requestId,
    user_id,
    ...stores
  } = StringHelper.safeJsonParse(socketData); // Safely parse JSON

  // Define store keys to exclude from logging
  const excludeStores = [
    StoreKeys.viewsFolderStore,
    StoreKeys.boardStore,
    StoreKeys.userPreferencesStore
  ];

  // Check if viewsStore contains only the default view, and exclude it if true
  if (stores[StoreKeys.viewsStore]?.rows) {
    const isOnlyDefaultView = stores[StoreKeys.viewsStore].rows.every((boardView) => {
      return boardView.is_default_view;
    });
    if (isOnlyDefaultView) {
      excludeStores.push(StoreKeys.viewsStore);
    }
  }

  // Remove the excluded stores from `stores`
  excludeStores.forEach((storeToExclude) => {
    delete stores[storeToExclude];
  });

  // If no relevant stores remain, exit early
  if (ObjectHelper.isEmpty(stores)) return;

  // Initialize the transaction queue in the global `window` object if not already present
  if (!window.swivuSocketTransactionQueue) {
    window.swivuSocketTransactionQueue = [];
  }

  // Maintain only the last 5 transactions in the queue
  if (window.swivuSocketTransactionQueue.length >= 5) {
    window.swivuSocketTransactionQueue.shift(); // Remove the oldest transaction
  }

  // Add the latest transaction to the queue
  // The latest data will be at last not first
  window.swivuSocketTransactionQueue.push({
    type,
    message,
    success,
    revision,
    board_session_id,
    requestId,
    user_id,
    ...stores
  });
};

export const getBryntumFloatRootElement = () => {
  let floatRoot = document.querySelector(".b-float-root");
  if (!floatRoot) {
    floatRoot = document.createElement("div");
    floatRoot.classList.add("b-float-root", "b-bryntum", "b-outer", "b-mac", "b-overlay-scrollbar", "b-chrome");
    document.body.appendChild(floatRoot);
  }

  return floatRoot;
}
